import React from 'react';
import { Button, Grid, Typography } from '@mui/material';
import { Form } from 'formik';
import * as Yup from 'yup';
import { MuiForm } from 'lib/Form';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { Row } from 'ui/Flex';
import { locationActionToLabel, LocationButtonActions } from '../models/LocationModel';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';

export interface LocationButtonFormData {
    index?: number;
    title?: string;
    action?: string;
    actionLabel?: string;
    style?: string;
    url?: string;
    properties?: {
        url?: string;
    };
}

interface LocationButtonFormProps {
    value?: Partial<LocationButtonFormData>;
    onSubmit: (value: Partial<LocationButtonFormData>) => void;
    close: () => void;
    formTitle: string;
}

interface LocationButtonFormValues {
    title: string;
    action: string;
    style: string;
    url: string;
}

const actionOptions = [
    { label: 'Browser', value: 'BROWSER' },
    { label: 'Check-In', value: 'CHECKIN' },
    { label: 'Opening Times', value: 'OPENING_TIMES' },
    { label: 'Events', value: 'EVENTS' },
    { label: 'Open Tab', value: 'OPEN_TAB' },
    { label: 'Preorder', value: 'PREORDER' }
];

const styleOptions = [
    { label: 'Primary', value: 'PRIMARY' },
    { label: 'Secondary', value: 'SECONDARY' }
];

const validationSchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    action: Yup.string().required('Action is required'),
    style: Yup.string().required('Style is required'),
    url: Yup.string().when('action', {
        is: 'BROWSER',
        then: schema => schema.url('Please enter a valid URL'),
        otherwise: schema => schema
    })
});

export const LocationButtonForm: React.FC<LocationButtonFormProps> = ({
    value,
    onSubmit,
    close,
    formTitle
}) => {
    const initialValues: LocationButtonFormValues = React.useMemo(
        () => ({
            title: value?.title ?? '',
            action: value?.action ?? 'BROWSER',
            style: value?.style ?? 'PRIMARY',
            url: value?.properties?.url ?? value?.url ?? ''
        }),
        [value]
    );

    const handleSubmit = React.useCallback(
        (values: LocationButtonFormValues) => {
            const result: Partial<LocationButtonFormData> = {
                title: values.title,
                action: values.action,
                actionLabel: locationActionToLabel[values.action as LocationButtonActions],
                style: values.style,
                properties: { url: values.url }
            };
            onSubmit(result);
            close();
        },
        [close, onSubmit]
    );

    return (
        <MuiForm<LocationButtonFormValues>
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({ values, submitForm, isValid }) => (
                <Form>
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <Typography variant="h6" color="primary">
                                {formTitle}
                            </Typography>
                        </Grid>
                        <Grid size={12}>
                            <SelectFormField
                                name="action"
                                label={<MuiFormLabel required>Action</MuiFormLabel>}
                                options={actionOptions}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextFormField name="title" label={<MuiFormLabel required>Title</MuiFormLabel>} />
                        </Grid>
                        <Grid size={12}>
                            <SelectFormField
                                name="style"
                                label={<MuiFormLabel required>Style</MuiFormLabel>}
                                options={styleOptions}
                            />
                        </Grid>
                        {values.action === 'BROWSER' && (
                            <Grid size={12}>
                                <TextFormField name="url" label="URL" />
                            </Grid>
                        )}
                        <Grid size={12}>
                            <Row gutter align="flex-end">
                                <Button onClick={close} variant="outlined">
                                    Cancel
                                </Button>
                                <Button
                                    onClick={submitForm}
                                    disabled={!isValid}
                                    variant="contained"
                                    color="primary"
                                >
                                    Save
                                </Button>
                            </Row>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </MuiForm>
    );
};

export const locationButtonFields = [
    { label: 'Title', width: '55%', key: 'title' },
    { label: 'Action', width: '30%', key: 'actionLabel' }
];
